import * as React from 'react'
import keyBy from 'lodash/keyBy'
import { featureCollection } from '@turf/helpers'
import { GeoJSONSource } from 'mapbox-gl'
import { useMap } from '../mapbox/MapContext'

const sourceId = 'place-source'
const placeMarkerLayerId = 'place-markers'
const placeTextLayerId = 'place-marker-text'
export const placeLayerIds = [placeMarkerLayerId, placeTextLayerId]
const clusterMaxZoom = 17

const accumulator = property => [
  ['case', ['to-boolean', ['get', 'is-selected']], ['get', property], ['coalesce', ['accumulated'], ['get', property]]],
  ['get', property],
]

const PlacesLayer = ({ currentPlace, places, onPlaceClick, onClusterClick, placeLabelStyle = 'text' }) => {
  const map = useMap()

  const features = React.useMemo(
    () =>
      featureCollection(
        places
          .filter(place => place.geo)
          // Trail/trailhead places render via dedicated geometry layers, not pin markers
          .filter(place => {
            const gt = place.geo.properties?.['geometry-type']
            return gt !== 'trail' && gt !== 'trailhead'
          })
          .map(place => ({
            ...place.geo,
            properties: {
              ...place.geo.properties,
              'is-selected': place.id === currentPlace?.id,
              'premium-image': place.mainImage?.publicId ?? null,
            },
          })),
      ),
    [places, currentPlace],
  )

  const placesById = React.useMemo(() => keyBy(places, 'id'), [places])

  // The latest features. The places can arrive before the style loads (or the style can be
  // reloaded later), so setup must add the source with these, not the ones seen at mount.
  const featuresRef = React.useRef(features)
  featuresRef.current = features

  // Add source and layers on mount
  React.useEffect(() => {
    if (!map) return
    // Wait for style to be loaded before adding sources/layers
    const setup = () => {
      if (!map || typeof map.getSource !== 'function') return
      if (map.getSource(sourceId)) return

      map.addSource(sourceId, {
        type: 'geojson',
        data: featuresRef.current,
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom,
        clusterProperties: {
          'marker-color': accumulator('marker-color'),
          title: accumulator('title'),
          'is-selected': ['coalesce', ['get', 'is-selected']],
        },
      })

      map.addLayer({
        id: placeMarkerLayerId,
        source: sourceId,
        type: 'symbol',
        layout: {
          'icon-image': [
            'case',
            ['has', 'point_count'],
            ['concat', 'marker_cluster', ['min', 3, ['get', 'point_count']], '_', ['get', 'marker-color']],
            ['to-boolean', ['get', 'premium-image']],
            ['concat', 'premium-marker_', ['get', 'marker-color'], '_', ['get', 'premium-image']],
            ['concat', 'marker_', ['get', 'marker-symbol'], '_', ['get', 'marker-color']],
          ],
          'icon-allow-overlap': true,
        },
        paint: {
          'icon-halo-width': 0,
        },
      })

      map.addLayer({
        id: placeTextLayerId,
        source: sourceId,
        type: 'symbol',
        layout: {
          'text-field': [
            'format',
            ['get', 'title'],
            {},
            ['case', ['has', 'point_count'], ['concat', '\n+', ['-', ['get', 'point_count'], 1], ' more'], ''],
            { 'font-scale': 0.8 },
          ],
          'text-variable-anchor': ['top', 'left', 'bottom', 'right'],
          'text-justify': 'auto',
          'text-radial-offset': ['case', ['to-boolean', ['get', 'premium-image']], 2, 1.5],
          'text-max-width': 9,
          'text-size': 13.5,
          'text-line-height': 1.33,
          'text-optional': true,
        },
        paint: {
          'text-color': ['get', 'marker-color'],
          'text-halo-width': 1,
          'text-halo-color': 'white',
        },
      })
    }

    // Wait for the style to finish loading before adding sources/layers.
    //
    // Callers hand us the map at different lifecycle points:
    //   - places_map_feed sets the map state inside m.on('load').
    //   - Mapbox.tsx (used by the public PlacesList) sets the map state
    //     immediately after `new mapboxgl.Map()`, before 'load' has fired.
    //
    // We can't use isStyleLoaded(): it ALSO flips false while any layer has
    // pending tile loads, which is the normal state immediately after
    // upstream code (boundaries, terrain, geometry layers) has added its own
    // layers. If isStyleLoaded happens to be false at mount and we only
    // register on 'style.load', we miss the fact that 'style.load' already
    // fired and setup never runs.
    //
    // map.getStyle() returns the style object only once the style has
    // finished its initial load (and keeps returning it thereafter), so
    // it's the right "has style loaded at least once?" check.
    //
    // We avoid 'idle' because a flyover's requestAnimationFrame loop
    // prevents 'idle' from ever firing and would deadlock setup.
    let styleReady = false
    try {
      styleReady = !!map.getStyle()?.layers
    } catch {
      styleReady = false
    }
    if (styleReady) {
      setup()
    }

    // Also (re-)run setup when the style (re-)loads — covers the pre-load
    // case above and later setStyle() calls which destroy all custom layers.
    const onStyleLoad = () => setup()
    map.on('style.load', onStyleLoad)

    return () => {
      try {
        map.off('style.load', onStyleLoad)
        if (map.getLayer(placeTextLayerId)) map.removeLayer(placeTextLayerId)
        if (map.getLayer(placeMarkerLayerId)) map.removeLayer(placeMarkerLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        // Map already destroyed by parent unmount
      }
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update data when features change
  React.useEffect(() => {
    if (!map) return
    const source = map.getSource(sourceId) as GeoJSONSource | undefined
    if (source) {
      source.setData(features)
    }
  }, [map, features])

  // Reactively apply the place-label style ('text' | 'pill' | 'none').
  // - 'text': colored text on a white halo (readable on light/streets maps)
  // - 'pill': white text surrounded by a thick marker-color halo that reads
  //    as a colored pill (best on satellite / dark styles)
  // - 'none': hide the text layer entirely; markers still render
  React.useEffect(() => {
    if (!map) return
    const applyStyle = () => {
      if (!map.getLayer(placeTextLayerId)) return
      const visibility = placeLabelStyle === 'none' ? 'none' : 'visible'
      map.setLayoutProperty(placeTextLayerId, 'visibility', visibility)
      if (placeLabelStyle === 'pill') {
        map.setPaintProperty(placeTextLayerId, 'text-color', '#ffffff')
        map.setPaintProperty(placeTextLayerId, 'text-halo-color', ['get', 'marker-color'])
        map.setPaintProperty(placeTextLayerId, 'text-halo-width', 3)
        map.setPaintProperty(placeTextLayerId, 'text-halo-blur', 0)
      } else {
        map.setPaintProperty(placeTextLayerId, 'text-color', ['get', 'marker-color'])
        map.setPaintProperty(placeTextLayerId, 'text-halo-color', '#ffffff')
        map.setPaintProperty(placeTextLayerId, 'text-halo-width', 1)
        map.setPaintProperty(placeTextLayerId, 'text-halo-blur', 0)
      }
    }
    applyStyle()
    // Re-apply on style reload since the text layer is re-added by our setup()
    map.on('style.load', applyStyle)
    return () => {
      map.off('style.load', applyStyle)
    }
  }, [map, placeLabelStyle])

  // Force symbol bucket rebuild when marker images finish async loading.
  // addMissingMarker() adds a colored placeholder synchronously, then loads the real
  // SVG/image async and fires 'markerImageUpdated'. triggerRepaint alone doesn't rebuild
  // symbol bucket vertex buffers — only setData() does. Coalesce events within a frame
  // to avoid thrashing when many markers finish at once.
  React.useEffect(() => {
    if (!map) return
    let scheduled = 0
    const rebuild = () => {
      scheduled = 0
      const source = map.getSource(sourceId) as GeoJSONSource | undefined
      if (source) source.setData(featuresRef.current)
    }
    const onUpdate = () => {
      if (scheduled) return
      scheduled = requestAnimationFrame(rebuild)
    }
    map.on('markerImageUpdated', onUpdate)
    return () => {
      map.off('markerImageUpdated', onUpdate)
      if (scheduled) cancelAnimationFrame(scheduled)
    }
  }, [map])

  // Click handler for clusters and places
  React.useEffect(() => {
    if (!map) return

    const handler = e => {
      if (e.features && e.features.length) {
        const [feature] = e.features
        if (feature.properties.cluster_id) {
          const source = map.getSource(sourceId) as GeoJSONSource
          source.getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
            if (!err) {
              onClusterClick(feature.geometry.coordinates, zoom + 2)
            }
          })
        } else {
          const place = placesById[feature.properties.id] ?? null
          onPlaceClick(place)
        }
      }
    }

    map.on('click', placeMarkerLayerId, handler)
    map.on('click', placeTextLayerId, handler)

    return () => {
      map.off('click', placeMarkerLayerId, handler)
      map.off('click', placeTextLayerId, handler)
    }
  }, [map, onPlaceClick, onClusterClick, placesById])

  // Cursor hover feedback
  React.useEffect(() => {
    if (!map) return

    const setCursor = cursor => {
      map.getCanvas().style.cursor = cursor
    }

    const enter = () => setCursor('pointer')
    const leave = () => setCursor('')

    map.on('mouseenter', placeMarkerLayerId, enter)
    map.on('mouseenter', placeTextLayerId, enter)
    map.on('mouseleave', placeMarkerLayerId, leave)
    map.on('mouseleave', placeTextLayerId, leave)

    return () => {
      map.off('mouseenter', placeMarkerLayerId, enter)
      map.off('mouseenter', placeTextLayerId, enter)
      map.off('mouseleave', placeMarkerLayerId, leave)
      map.off('mouseleave', placeTextLayerId, leave)
    }
  }, [map])

  return null
}

export default React.memo(PlacesLayer)
